import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getJobs, createJob, getClients } from "./opsApi";
import { Film, Upload } from "lucide-react";

const inp = "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[#AC3333]";
const badge = { en_cola: "bg-amber-500/20 text-amber-300", listo: "bg-emerald-500/20 text-emerald-300", pendiente: "bg-slate-500/20 text-slate-300", procesando: "bg-amber-500/20 text-amber-300", completado: "bg-emerald-500/20 text-emerald-300", error: "bg-red-500/20 text-red-300" };

export default function OpsVideo() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ client_id: "", artista: "", lanzamiento: "", inicio: "00:00:00", duracion: 15 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { getJobs().then(setJobs).catch(() => {}); getClients().then(setClients).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.client_id || !f.artista.trim() || !f.lanzamiento.trim()) { toast.error("Completa cliente, artista y lanzamiento"); return; }
    if (!file) { toast.error("Sube el MP3"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("El MP3 supera 15 MB"); return; }
    if (f.duracion < 5 || f.duracion > 90) { toast.error("Duración entre 5 y 90 s"); return; }
    const fd = new FormData();
    fd.append("client_id", f.client_id); fd.append("artista", f.artista); fd.append("lanzamiento", f.lanzamiento);
    fd.append("inicio", f.inicio); fd.append("duracion", String(f.duracion)); fd.append("audio", file);
    setBusy(true);
    try {
      const job = await createJob(fd);
      toast[job.status === "error" ? "error" : "success"](job.status === "error" ? (job.mensaje || "Error en n8n") : "Trabajo enviado");
      setFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const cname = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="ops-video">
      <h2 className="mb-5 flex items-center gap-2 font-serif text-2xl font-bold text-white"><Film className="h-6 w-6 text-[#AC3333]" /> Producción de Video (Reels)</h2>
      <div className="mb-6 grid gap-3 rounded-2xl border border-white/10 bg-[#241010] p-5 sm:grid-cols-2">
        <div><label className="mb-1 block text-xs text-white/50">Cliente</label>
          <select data-testid="ops-video-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inp}><option value="">—</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div><label className="mb-1 block text-xs text-white/50">Artista</label><input data-testid="ops-video-artista" value={f.artista} onChange={(e) => setF({ ...f, artista: e.target.value })} className={inp} /></div>
        <div><label className="mb-1 block text-xs text-white/50">Lanzamiento</label><input data-testid="ops-video-lanzamiento" value={f.lanzamiento} onChange={(e) => setF({ ...f, lanzamiento: e.target.value })} className={inp} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="mb-1 block text-xs text-white/50">Inicio (HH:MM:SS)</label><input data-testid="ops-video-inicio" value={f.inicio} onChange={(e) => setF({ ...f, inicio: e.target.value.replace(/[^\d:]/g, "").slice(0, 8) })} className={`${inp} font-mono`} /></div>
          <div><label className="mb-1 block text-xs text-white/50">Duración (s)</label><input data-testid="ops-video-duracion" type="number" min={5} max={90} value={f.duracion} onChange={(e) => setF({ ...f, duracion: Number(e.target.value) })} className={inp} /></div>
        </div>
        <div className="sm:col-span-2">
          <label data-testid="ops-video-file" className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 py-2.5 text-sm text-white/60 hover:border-[#AC3333]">
            <Upload className="h-4 w-4" /> {file ? file.name : "Sube el MP3 (máx. 15 MB)"}
            <input type="file" accept="audio/mpeg,audio/mp3" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div className="sm:col-span-2">
          <button data-testid="ops-video-submit" onClick={submit} disabled={busy} className="w-full rounded-lg bg-[#AC3333] py-2.5 text-sm font-semibold text-white hover:bg-[#FF0505] disabled:opacity-60">{busy ? "Enviando a n8n…" : "Generar y publicar Reel"}</button>
        </div>
      </div>
      <h3 className="mb-3 font-serif text-lg font-bold text-white">Trabajos</h3>
      <div className="space-y-2">
        {jobs.map((j) => (
          <div key={j.id} data-testid={`ops-job-${j.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-[#241010] p-3">
            <span className={`rounded-full px-2 py-0.5 text-[11px] ${badge[j.status] || badge.en_cola}`}>{j.status}</span>
            <span className="flex-1 text-sm text-white">{j.artista} — {j.lanzamiento}</span>
            <span className="text-xs text-white/40">{cname(j.client_id)} · {j.inicio} · {j.duracion}s</span>
            {j.video_url && <a href={j.video_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#FF7a7a] hover:underline">Ver video</a>}
            {j.mensaje && <span className="w-full text-[11px] text-white/40">{j.mensaje}</span>}
          </div>
        ))}
        {jobs.length === 0 && <p className="text-sm text-white/40">Aún no hay trabajos.</p>}
      </div>
    </div>
  );
}
